import Cookies from "universal-cookie";

/**
 * Enterprise Production-Grade Verisoul Web SDK Integration
 *
 * Key Capabilities:
 * 1. Zero Crash Guarantee: Never throws unhandled exceptions or causes unhandled promise rejections.
 * 2. Absolute Isolation: SDK failures, ad-blockers, CDN 404s, or timeouts never block application workflows.
 * 3. Strict Timeout & Circuit Breaker: Automatically times out slow/hanging requests and enters a fast-fail cooldown.
 * 4. Multi-Tier Cache (Memory -> SessionStorage -> LocalStorage -> Cookie):
 *    Permanently caches session ID for the user (NO TTL) so Verisoul is NEVER called multiple times for the same user.
 * 5. In-Flight Request Deduplication: Merges concurrent calls into a single in-flight promise.
 * 6. Automatic User Identity Resolution: Resolves user identifiers from parameters, cookies, or URL params.
 * 7. Background Pre-warming: Supports non-blocking early SDK initialization.
 */

const cookies = new Cookies();

// Configuration
const CONFIG = {
    SCRIPT_ID: "verisoul-sdk-script",
    DEFAULT_SCRIPT_URL: "https://js.verisoul.ai/prod/bundle.js",
    DEFAULT_PROJECT_ID: "4dedc624-0acb-45ad-8933-09850a28d453",
    DEFAULT_TIMEOUT_MS: 3500, // 3.5 seconds maximum wait
    CIRCUIT_BREAKER_BASE_COOLDOWN_MS: 15000, // 15 seconds cooldown on failure
    CIRCUIT_BREAKER_MAX_COOLDOWN_MS: 60000, // 60 seconds maximum cooldown
    STORAGE_PREFIX: "vs_sess_",
    COOKIE_PREFIX: "vs_sess_",
};

// Memory Cache: Map<cacheKey, sessionId: string>
const memoryCache = new Map();

// In-Flight Promise Map: Map<cacheKey, Promise<string>>
const inFlightPromises = new Map();

// Circuit Breaker State
const circuitBreaker = {
    consecutiveFailures: 0,
    lastFailureTime: 0,

    isOpen() {
        if (this.consecutiveFailures < 2) return false;
        const elapsed = Date.now() - this.lastFailureTime;
        const cooldown = Math.min(
            CONFIG.CIRCUIT_BREAKER_BASE_COOLDOWN_MS * this.consecutiveFailures,
            CONFIG.CIRCUIT_BREAKER_MAX_COOLDOWN_MS
        );
        return elapsed < cooldown;
    },

    recordSuccess() {
        this.consecutiveFailures = 0;
        this.lastFailureTime = 0;
    },

    recordFailure() {
        this.consecutiveFailures += 1;
        this.lastFailureTime = Date.now();
    },

    reset() {
        this.consecutiveFailures = 0;
        this.lastFailureTime = 0;
    },
};

/**
 * Validate that a session ID is a legitimate, non-empty string.
 */
const isValidSessionId = (sessionId) => {
    return (
        typeof sessionId === "string" &&
        sessionId.trim().length > 0 &&
        sessionId.length <= 512
    );
};

/**
 * Resolve effective project ID.
 */
const resolveProjectId = (projectId) => {
    return (
        (projectId && typeof projectId === "string" && projectId.trim()) ||
        (process.env.REACT_APP_VERISOUL_PROJECT_ID && process.env.REACT_APP_VERISOUL_PROJECT_ID.trim()) ||
        CONFIG.DEFAULT_PROJECT_ID
    );
};

/**
 * Resolve effective script URL (supports reverse proxies or custom CDN endpoints).
 */
const resolveScriptUrl = () => {
    return (
        (process.env.REACT_APP_VERISOUL_SCRIPT_URL && process.env.REACT_APP_VERISOUL_SCRIPT_URL.trim()) ||
        CONFIG.DEFAULT_SCRIPT_URL
    );
};

/**
 * Resolve user identifier for session binding.
 * Tries: explicit argument -> cookies -> URL params -> anonymous fallback.
 */
const resolveUserIdentifier = (explicitUserId) => {
    if (explicitUserId && typeof explicitUserId === "string" && explicitUserId.trim()) {
        return explicitUserId.trim();
    }

    try {
        if (typeof window !== "undefined") {
            // 1. Universal cookies
            const cookieUser = cookies.get("userId") || cookies.get("fingerprint_id");
            if (cookieUser && String(cookieUser).trim() && cookieUser !== "null" && cookieUser !== "undefined") {
                return String(cookieUser).trim();
            }

            // 2. URL Query Params (common in survey platforms)
            if (window.location && window.location.search) {
                const params = new URLSearchParams(window.location.search);
                const vid = params.get("vid") || params.get("supplierCode") || params.get("uid");
                if (vid && vid.trim()) {
                    return vid.trim();
                }
            }
        }
    } catch (e) {
        // Suppress any environment/URL parsing error
    }

    return "session_user";
};

/**
 * Generate namespaced storage keys.
 */
const getStorageKey = (projectId, userId) => {
    const safeProj = (projectId || "default").replace(/[^a-zA-Z0-9_-]/g, "");
    const safeUser = (userId || "user").replace(/[^a-zA-Z0-9_-]/g, "");
    return `${CONFIG.STORAGE_PREFIX}${safeProj}_${safeUser}`;
};

/**
 * Multi-Tier Cache Reader:
 * Tier 1: In-Memory Map (0ms)
 * Tier 2: sessionStorage (Fast, tab-isolated)
 * Tier 3: localStorage (Persists across tab closures / reloads)
 * Tier 4: Cookies (Fallback if Web Storage is disabled by privacy settings)
 * Note: Permanent caching, NO TTL.
 */
const getCachedSession = (storageKey) => {
    // Tier 1: In-Memory Cache
    const inMem = memoryCache.get(storageKey);
    if (inMem && isValidSessionId(inMem)) {
        return inMem;
    }

    if (typeof window === "undefined") {
        return null;
    }

    // Helper to extract session ID from raw value or JSON object
    const extractSessionId = (raw) => {
        if (!raw) return null;
        try {
            let extracted = raw;
            if (typeof raw === "string" && (raw.startsWith("{") || raw.startsWith('"'))) {
                try {
                    const parsed = JSON.parse(raw);
                    extracted = parsed?.sessionId || parsed;
                } catch (_) {
                    extracted = raw;
                }
            } else if (typeof raw === "object" && raw?.sessionId) {
                extracted = raw.sessionId;
            }

            if (isValidSessionId(extracted)) {
                // Populate Memory Cache
                memoryCache.set(storageKey, String(extracted).trim());
                return String(extracted).trim();
            }
        } catch (e) {
            // Ignore parse errors
        }
        return null;
    };

    // Tier 2: sessionStorage
    try {
        const fromSession = window.sessionStorage?.getItem(storageKey);
        const validSession = extractSessionId(fromSession);
        if (validSession) return validSession;
    } catch (e) {
        // sessionStorage restricted (e.g. private mode or iframe sandboxing)
    }

    // Tier 3: localStorage
    try {
        const fromLocal = window.localStorage?.getItem(storageKey);
        const validLocal = extractSessionId(fromLocal);
        if (validLocal) {
            // Re-sync to sessionStorage
            try {
                window.sessionStorage?.setItem(storageKey, validLocal);
            } catch (_) { }
            return validLocal;
        }
    } catch (e) {
        // localStorage restricted
    }

    // Tier 4: Cookies
    try {
        const fromCookie = cookies.get(storageKey);
        const validCookie = extractSessionId(fromCookie);
        if (validCookie) {
            return validCookie;
        }
    } catch (e) {
        // Cookies restricted
    }

    return null;
};

/**
 * Multi-Tier Cache Writer:
 * Saves the session ID permanently across memory, sessionStorage, localStorage, and cookies.
 * No TTL / No expiration.
 */
const setCachedSession = (storageKey, sessionId) => {
    if (!isValidSessionId(sessionId)) return;

    const cleanSessionId = String(sessionId).trim();

    // Tier 1: In-Memory
    memoryCache.set(storageKey, cleanSessionId);

    if (typeof window === "undefined") return;

    // Tier 2: sessionStorage
    try {
        window.sessionStorage?.setItem(storageKey, cleanSessionId);
    } catch (e) {
        // Ignore quota/permission errors
    }

    // Tier 3: localStorage
    try {
        window.localStorage?.setItem(storageKey, cleanSessionId);
    } catch (e) {
        // Ignore quota/permission errors
    }

    // Tier 4: Cookie (persistent, no TTL)
    try {
        // Long-lived persistent cookie without TTL
        const farFuture = new Date();
        farFuture.setFullYear(farFuture.getFullYear() + 5);

        cookies.set(storageKey, cleanSessionId, {
            path: "/",
            expires: farFuture,
            sameSite: "lax",
        });
    } catch (e) {
        // Ignore cookie errors
    }
};

/**
 * Safely load the Verisoul JavaScript SDK.
 * Handles DOM readiness, prevents duplicate tags, cleans up dead nodes on error.
 */
const loadVerisoulSdk = (projectId) => {
    return new Promise((resolve, reject) => {
        try {
            if (typeof window === "undefined" || typeof document === "undefined") {
                return reject(new Error("DOM environment not available"));
            }

            // 1. If SDK is already attached to window, resolve immediately
            if (window.Verisoul && typeof window.Verisoul.session === "function") {
                return resolve(window.Verisoul);
            }

            // 2. Check for existing script tag
            let script = document.getElementById(CONFIG.SCRIPT_ID);

            if (script) {
                // If script exists and window.Verisoul is ready
                if (window.Verisoul && typeof window.Verisoul.session === "function") {
                    return resolve(window.Verisoul);
                }

                // If currently loading, attach one-time event handlers
                const onExistingLoad = () => {
                    if (window.Verisoul && typeof window.Verisoul.session === "function") {
                        resolve(window.Verisoul);
                    } else {
                        reject(new Error("Verisoul object missing after script load"));
                    }
                };

                const onExistingError = () => {
                    reject(new Error("Existing Verisoul script failed to load"));
                };

                script.addEventListener("load", onExistingLoad, { once: true });
                script.addEventListener("error", onExistingError, { once: true });
                return;
            }

            // 3. Create fresh script tag
            script = document.createElement("script");
            script.id = CONFIG.SCRIPT_ID;
            script.src = resolveScriptUrl();
            script.async = true;
            script.crossOrigin = "anonymous";
            script.referrerPolicy = "no-referrer-when-downgrade";

            if (projectId) {
                script.setAttribute("verisoul-project-id", projectId);
            }

            script.onload = () => {
                if (window.Verisoul && typeof window.Verisoul.session === "function") {
                    resolve(window.Verisoul);
                } else {
                    reject(new Error("Verisoul SDK loaded but session() method is missing"));
                }
            };

            script.onerror = () => {
                // Clean up broken node
                try {
                    script.remove();
                } catch (_) { }
                reject(new Error("Failed to load Verisoul script (blocked by network or ad-blocker)"));
            };

            const target = document.head || document.body || document.documentElement;
            target.appendChild(script);
        } catch (err) {
            reject(err);
        }
    });
};

/**
 * Retrieve the Verisoul session ID with production-grade reliability.
 *
 * Guarantees:
 * - NEVER crashes the application or throws uncaught promise rejections.
 * - If Verisoul is blocked, crashes, or times out, safely returns '' so all other functions continue.
 * - Caches the session ID permanently without TTL (Memory, SessionStorage, LocalStorage, Cookies).
 * - Deduplicates concurrent calls.
 * - Does NOT call Verisoul multiple times for the same user.
 *
 * @param {string} [projectId] Verisoul Project ID (defaults to env or project config)
 * @param {string} [userId] Optional user/visitor identifier for cache binding
 * @returns {Promise<string>} Valid sessionId or empty string ''
 */
export const getVerisoulSessionId = async (projectId = null, userId = null) => {
    try {
        const resolvedProjectId = resolveProjectId(projectId);
        const resolvedUserId = resolveUserIdentifier(userId);
        const storageKey = getStorageKey(resolvedProjectId, resolvedUserId);

        // 1. FAST PATH: Check multi-tier cache for existing session (NO TTL)
        const cachedSessionId = getCachedSession(storageKey);
        if (cachedSessionId) {
            console.log(`[Verisoul] Reusing cached session ID for user (${resolvedUserId}):`, cachedSessionId);
            return cachedSessionId;
        }

        // Also check generic key if specific user had none (e.g. before login/identification)
        if (resolvedUserId !== "session_user") {
            const genericKey = getStorageKey(resolvedProjectId, "session_user");
            const genericSession = getCachedSession(genericKey);
            if (genericSession) {
                console.log(`[Verisoul] Found session in general cache, linking to user (${resolvedUserId}):`, genericSession);
                // Associate permanently with specific user
                setCachedSession(storageKey, genericSession);
                return genericSession;
            }
        }

        // 2. CONCURRENCY: Check for an existing in-flight promise for this key
        if (inFlightPromises.has(storageKey)) {
            return inFlightPromises.get(storageKey);
        }

        // 3. CIRCUIT BREAKER: Fail fast if recently failed (e.g. ad-blocker active)
        if (circuitBreaker.isOpen()) {
            console.warn("[Verisoul] Circuit breaker open due to recent failures. Skipping Verisoul call.");
            return "";
        }

        console.log(`[Verisoul] Requesting session for user (${resolvedUserId}) with Project ID:`, resolvedProjectId);

        // 4. IN-FLIGHT EXECUTION: Deduplicate and execute with timeout
        const fetchPromise = (async () => {
            try {
                // Execution promise
                const executionPromise = (async () => {
                    await loadVerisoulSdk(resolvedProjectId);

                    if (!window.Verisoul || typeof window.Verisoul.session !== "function") {
                        throw new Error("Verisoul SDK is unavailable");
                    }

                    const result = await window.Verisoul.session();

                    if (!result || !result.session_id) {
                        throw new Error("Verisoul session_id missing from response");
                    }

                    return String(result.session_id).trim();
                })();

                // Strict Timeout Promise
                const timeoutMs =
                    parseInt(process.env.REACT_APP_VERISOUL_TIMEOUT_MS, 10) || CONFIG.DEFAULT_TIMEOUT_MS;

                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => {
                        reject(new Error(`Verisoul SDK timed out after ${timeoutMs}ms`));
                    }, timeoutMs);
                });

                // Race SDK execution against timeout
                const sessionId = await Promise.race([executionPromise, timeoutPromise]);

                if (isValidSessionId(sessionId)) {
                    circuitBreaker.recordSuccess();
                    console.log("[Verisoul] Successfully generated session ID:", sessionId);

                    // Permanently cache for this user (NO TTL)
                    setCachedSession(storageKey, sessionId);

                    // Also cache to generic session key
                    if (resolvedUserId !== "session_user") {
                        setCachedSession(getStorageKey(resolvedProjectId, "session_user"), sessionId);
                    }

                    return sessionId;
                }

                return "";
            } catch (err) {
                circuitBreaker.recordFailure();
                console.warn(
                    "[Verisoul SDK] Session retrieval error:",
                    err?.message || err
                );

                return "";
            } finally {
                inFlightPromises.delete(storageKey);
            }
        })();

        inFlightPromises.set(storageKey, fetchPromise);
        return await fetchPromise;
    } catch (topLevelError) {
        // Absolute fail-safe
        return "";
    }
};

/**
 * Proactive background initialization (pre-warming).
 * Calls getVerisoulSessionId in the background without blocking the UI.
 * Recommended by Verisoul docs: allows behavioral signals to be gathered early.
 *
 * @param {string} [projectId]
 * @param {string} [userId]
 */
export const initVerisoul = (projectId = null, userId = null) => {
    try {
        if (typeof window === "undefined") return;
        // Schedule in idle callback or microtask so initial render is completely unaffected
        const runner = () => {
            getVerisoulSessionId(projectId, userId).catch(() => { });
        };

        if (typeof window.requestIdleCallback === "function") {
            window.requestIdleCallback(runner, { timeout: 2000 });
        } else {
            setTimeout(runner, 100);
        }
    } catch (_) {
        // Ignore
    }
};

/**
 * Synchronous check whether a valid session is already cached for the given user.
 */
export const isVerisoulCached = (projectId = null, userId = null) => {
    try {
        const resolvedProjectId = resolveProjectId(projectId);
        const resolvedUserId = resolveUserIdentifier(userId);
        const storageKey = getStorageKey(resolvedProjectId, resolvedUserId);
        return Boolean(getCachedSession(storageKey));
    } catch (_) {
        return false;
    }
};

/**
 * Clear cached Verisoul session for the given user or globally.
 */
export const clearVerisoulSession = (projectId = null, userId = null) => {
    try {
        const resolvedProjectId = resolveProjectId(projectId);
        const resolvedUserId = resolveUserIdentifier(userId);
        const storageKey = getStorageKey(resolvedProjectId, resolvedUserId);
        const genericKey = getStorageKey(resolvedProjectId, "session_user");

        memoryCache.delete(storageKey);
        memoryCache.delete(genericKey);

        if (typeof window !== "undefined") {
            window.sessionStorage?.removeItem(storageKey);
            window.sessionStorage?.removeItem(genericKey);
            window.localStorage?.removeItem(storageKey);
            window.localStorage?.removeItem(genericKey);
        }

        cookies.remove(storageKey, { path: "/" });
        cookies.remove(genericKey, { path: "/" });
        circuitBreaker.reset();
    } catch (error) {
        // Ignore
        console.error(error)
    }
};

if (typeof window !== "undefined") {
    window.__clearVerisoulSession = clearVerisoulSession;
}

export default {
    getVerisoulSessionId,
    initVerisoul,
    isVerisoulCached,
    clearVerisoulSession,
};
